<template>
  <div class="game-view">
    <div id="game-canvas" ref="gameCanvas"></div>
    
    <!-- Add FPS Counter -->
    <FPSCounter />
    
    <!-- Join Game Overlay - show when player hasn't joined yet -->
    <div v-if="!playerJoined" class="join-game-overlay">
      <div class="join-game-form">
        <h2>Join Game</h2>
        <div class="form-description">
          <p>Welcome to the arena! Enter a name to join the action.</p>
        </div>
        <div class="form-group">
          <label for="username">Username (optional):</label>
          <input 
            type="text" 
            id="username" 
            v-model="joinUsername" 
            placeholder="Enter your username"
            @keyup.enter="handleJoinGame"
          />
        </div>
        <div class="username-preview" v-if="displayJoinName">
          You will play as: <strong>{{ displayJoinName }}</strong>
        </div>
        <div class="join-actions">
          <button class="join-button" @click="handleJoinGame">Join Game</button>
        </div>
      </div>
    </div>
    
    <!-- Crosshair element - only show when game is active and player has joined -->
    <div v-if="!isGameOver && !hasError && playerJoined" 
         class="crosshair" 
         :class="{ 'crosshair-shooting': isShooting }">
      <div class="crosshair-inner"></div>
    </div>
    
    <HUD
      v-if="!hasError && playerJoined"
      :player-health="playerHealth"
      :max-health="maxHealth"
      :match-time-seconds="matchTimeSeconds"
      :current-weapon="currentWeapon"
      :available-weapons="availableWeapons"
      :current-ammo="currentAmmo"
      :max-ammo="maxAmmo"
      :show-countdown="showCountdown"
      :countdown-seconds="matchCountdown"
      :game-mode="'FreeForAll'"
    />
    <WeaponHUD
      v-if="!hasError && playerJoined"
      :current-weapon="weaponType"
      :current-ammo="weaponAmmo"
      :total-ammo="totalAmmo"
      :is-reloading="isReloading"
      :is-switching="isSwitching"
      @weapon-selected="switchWeapon"
    />
    <Scoreboard
      v-if="!hasError && isScoreboardVisible"
      :is-visible="isScoreboardVisible"
      :players="players"
      :current-player-id="currentPlayerId"
    />
    <!-- Notification system -->
    <div v-if="notification.visible" :class="['game-notification', `notification-${notification.type}`]">
      {{ notification.message }}
    </div>
    
    <!-- Spawn protection indicator -->
    <div v-if="hasSpawnProtection" class="spawn-protection-indicator">
      <div class="protection-icon">🛡️</div>
      <div class="protection-text">Spawn Protection Active</div>
      <div v-if="spawnProtectionTimeLeft > 0" class="protection-timer">{{ spawnProtectionTimeLeft }}s</div>
      <div class="protection-note">Protection ends if you shoot</div>
    </div>
    
    <!-- Waiting for players message -->
    <div v-if="!matchInProgress && !showCountdown && !isGameOver && !hasError && !enoughPlayers" class="waiting-message-container">
      <div class="waiting-message">
        <h2>Waiting for Players</h2>
        <div class="player-count">
          <span>Players: {{ getTotalPlayerCount() }}/2 minimum</span>
        </div>
        <div class="connected-players" v-if="players.length > 0">
          <p>Connected players:</p>
          <ul>
            <li v-for="player in players" :key="player.id">
              {{ player.username }} 
              <span v-if="player.id === currentPlayerId">(You)</span>
            </li>
          </ul>
        </div>
        <button class="refresh-button" @click="refreshPlayerList">Refresh Players</button>
        <p>Game will start automatically when enough players join</p>
      </div>
    </div>
    
    <!-- Game over screen with match stats and next game countdown -->
    <div v-if="isGameOver && !hasError" class="game-stats-screen">
      <h2>Game Over</h2>
      <h3>{{ winner }} Wins!</h3>
      
      <!-- Game stats (like the scoreboard) -->
      <div class="match-stats-container">
        <div class="match-info">
          <div class="info-box">
            <span class="info-label">Map:</span>
            <span class="info-value">{{ currentMap }}</span>
          </div>
          <div class="info-box">
            <span class="info-label">Duration:</span>
            <span class="info-value">{{ formatTime(matchDuration) }}</span>
          </div>
        </div>
        
        <!-- Player scoreboard -->
        <table class="player-stats-table">
          <thead>
            <tr>
              <th class="rank">#</th>
              <th class="player-col">Player</th>
              <th>K</th>
              <th>D</th>
              <th>KD</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(player, index) in sortedPlayersByScore" 
              :key="player.id"
              :class="{ 'current-player': player.id === currentPlayerId }"
            >
              <td class="rank">{{ index + 1 }}</td>
              <td class="player-col">
                <span class="player-name">{{ player.username }}</span>
              </td>
              <td>{{ player.kills }}</td>
              <td>{{ player.deaths }}</td>
              <td>{{ (player.kills / Math.max(player.deaths, 1)).toFixed(2) }}</td>
              <td class="score-cell">{{ player.score }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Next game countdown -->
      <div class="next-game-countdown">
        <h3>Next Game Starting In: {{ nextGameCountdown }}s</h3>
        <div class="progress-bar">
          <div class="progress" :style="{ width: `${(nextGameCountdown / 20) * 100}%` }"></div>
        </div>
      </div>
    </div>
    
    <div v-if="hasError" class="game-error">
      <h2>Game Error</h2>
      <p>{{ errorMessage || 'An error occurred while loading the game' }}</p>
      <p>Attempting to recover...</p>
      <button @click="tryAgain">Try Again</button>
      <button @click="exitToLobby">Back to Lobby</button>
    </div>
  </div>
</template>

<script>
import MainScene from '../scenes/MainScene';
import HUD from '../components/HUD.vue';
import WeaponHUD from '../components/WeaponHUD.vue';
import Scoreboard from '../components/Scoreboard.vue';
import FPSCounter from '../components/FPSCounter.vue';
import { shallowRef, defineComponent } from 'vue';
import { WEAPON_TYPES } from '../weapons';

// Create the scene ref outside of the component to ensure it's available
const mainSceneRef = shallowRef(null);

export default defineComponent({
  name: 'GameView',
  components: {
    HUD,
    WeaponHUD,
    Scoreboard,
    FPSCounter
  },
  props: {
    playerData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      // Character attributes
      characterAttributes: {
        soldier: {
          name: 'Soldier',
          health: 100,
          speed: 6,
          respawnTime: 3
        },
        scout: {
          name: 'Scout',
          health: 80,
          speed: 9,
          respawnTime: 3
        }
      },
      // Local copy of player data that can be modified
      localPlayerData: null,
      // Key press tracking
      keysPressed: {},
      tabKeyHandled: false, // Track if tab key is currently being handled
      // Game state
      isGameOver: false,
      isScoreboardVisible: false,
      winner: null,
      
      // Crosshair state
      isShooting: false,
      crosshairTimeout: null,
      
      // Match information
      playerScores: {},
      matchTimeSeconds: 300, // 5 minutes default
      matchInProgress: false,
      matchCountdown: 0,
      showCountdown: false,
      
      // Player information
      currentPlayerId: '', // Will be assigned by server
      playerHealth: 100,
      maxHealth: 100,
      currentWeapon: 'Rifle',
      availableWeapons: ['Rifle', 'Pistol', 'Knife'],
      currentAmmo: 30,
      maxAmmo: 90,
      
      // Weapon information
      weaponType: 'machineGun',
      weaponAmmo: 30,
      totalAmmo: 90,
      isReloading: false,
      isSwitching: false,
      
      // Hitmarker state
      showHitmarker: false,
      hitmarkerTimeout: null,
      
      // Spawn protection
      hasSpawnProtection: false,
      spawnProtectionTimeLeft: 0,
      spawnProtectionInterval: null,
      
      // Scene management
      hasInitialized: false,
      hasError: false,
      errorMessage: '',
      
      // Notification system
      notification: {
        visible: false,
        message: '',
        type: 'info',
        timeout: null
      },
      
      // Player management 
      players: [],
      playerStats: {
        players: 0,
        total: 0,
        enoughPlayers: false
      },
      enoughPlayers: false,
      
      // Match summary data
      matchSummaryData: null,
      
      // Timer for weapon updates
      weaponUpdateTimer: null,
      
      // Player list refresh
      lastRefreshTime: null,
      playerListRefreshInterval: null,
      
      // Game end and restart
      nextGameCountdown: 20,
      nextGameInterval: null,
      matchDuration: 0,
      currentMap: 'Arena', // Replace with actual map name when available
      
      // Join game form
      playerJoined: false,
      joinUsername: '',
      randomId: Math.floor(1000 + Math.random() * 9000),
    };
  },
  computed: {
    // Add a computed property to check if both teams have players
    bothTeamsHavePlayers() {
      return this.teamStats.bothTeamsHavePlayers;
    },
    sortedPlayersByScore() {
      return [...this.players].sort((a, b) => {
        // Calculate K/D ratios (avoid division by zero)
        const aKD = a.kills / Math.max(a.deaths, 1);
        const bKD = b.kills / Math.max(b.deaths, 1);
        
        // First sort by K/D ratio
        if (bKD !== aKD) {
          return bKD - aKD;
        }
        
        // If K/D is the same, sort by join timestamp (earlier joins first)
        return (a.joinTimestamp || 0) - (b.joinTimestamp || 0);
      });
    },
    
    // Display name for join form
    displayJoinName() {
      if (this.joinUsername.trim()) {
        return `${this.joinUsername.trim()}-${this.randomId}`;
      } else {
        return `Player-${this.randomId}`;
      }
    },
  },
  mounted() {
    console.log('GameView mounted with player data:', this.playerData);
    
    // Initialize localPlayerData from props if available
    if (this.playerData) {
      this.localPlayerData = { ...this.playerData };
    }
    
    // Set initial health based on character type
    if (this.playerData && this.playerData.character) {
      const characterType = this.playerData.character;
      if (this.characterAttributes[characterType]) {
        this.maxHealth = this.characterAttributes[characterType].health;
        this.playerHealth = this.maxHealth;
      }
    }
    
    try {
      // Initialize the game scene first
      this.initGame();
      
      // Set up controls for mouse and keyboard
      this.setupControls();
      
      // Store reference to the main scene for easier access
      this.mainScene = mainSceneRef.value;
      
      // Only set up event listeners if game initialization was successful
      if (!this.hasError) {
        this.setupEventListeners();
        
        // Set up timer to update weapon status from the MainScene with a slight delay
        // to ensure the weapon system is properly initialized
        setTimeout(() => {
          console.log('Setting up weapon update timer...');
          this.weaponUpdateTimer = setInterval(() => {
            this.updateWeaponStatus();
          }, 100);
        }, 1000); // 1 second delay
      }
      
      // Set playerJoined to true if player data was passed from parent component
      if (this.playerData) {
        this.playerJoined = true;
      }
      
      // Listen for health update events
      window.addEventListener('player-health-update', this.onHealthUpdate);
      
      // Listen for respawn events
      window.addEventListener('player-respawn', this.onPlayerRespawn);
      
      // Listen for player shooting to clear spawn protection
      window.addEventListener('player-shoot', this.onPlayerShoot);
      
      // Listen for match start events (from MainScene)
      window.addEventListener('match-start', this.onMatchStart);
      
      // Listen for match end events (from MainScene)
      window.addEventListener('match-end', this.onMatchEnd);
      
      // Add custom event listeners for player management
      window.addEventListener('player-joined', this.onPlayerJoined);
      window.addEventListener('player-created', this.onPlayerCreated);
      window.addEventListener('game-joined', this.onGameJoined);
      window.addEventListener('existing-players-received', this.onExistingPlayersReceived);
      window.addEventListener('player-stats-update', this.onPlayerStatsUpdate);
      window.addEventListener('player-hit', this.onPlayerHit);
      
      // Setup periodic player list refresh when not in a match
      // This keeps the waiting room view updated with current players
      this.lastRefreshTime = Date.now();
      this.playerListRefreshInterval = setInterval(() => {
        if (mainSceneRef.value && mainSceneRef.value.socket && 
            mainSceneRef.value.socket.connected && !this.matchInProgress) {
          // Only refresh if it's been at least 5 seconds since last refresh
          const now = Date.now();
          if (now - this.lastRefreshTime >= 5000) {
            console.log('Requesting periodic player list refresh');
            mainSceneRef.value.socket.emit('request-existing-players');
            this.lastRefreshTime = now;
          }
        }
        
        // Periodically clean up duplicate players
        this.cleanupDuplicatePlayers();
      }, 5000); // Reduced frequency from 3s to 5s
      
    } catch (error) {
      console.error('Error initializing game:', error);
      this.handleError(error);
    }
  },
  beforeUnmount() {
    console.log('GameView unmounting, cleaning up...');
    
    // Clean up timers
    if (this.weaponUpdateTimer) clearInterval(this.weaponUpdateTimer);
    if (this.playerListRefreshInterval) clearInterval(this.playerListRefreshInterval);
    if (this.spawnProtectionInterval) clearInterval(this.spawnProtectionInterval);
    
    // Clean up event listeners
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('click', this.onMouseClick);
    document.removeEventListener('mousemove', this.onMouseMove);
    
    // Remove custom event listeners
    window.removeEventListener('weapon-system-ready', this.onWeaponSystemReady);
    
    // Remove all event listeners
    window.removeEventListener('player-health-update', this.onHealthUpdate);
    window.removeEventListener('player-respawn', this.onPlayerRespawn);
    window.removeEventListener('player-shoot', this.onPlayerShoot);
    window.removeEventListener('match-start', this.onMatchStart);
    window.removeEventListener('match-end', this.onMatchEnd);
    window.removeEventListener('player-joined', this.onPlayerJoined);
    window.removeEventListener('player-created', this.onPlayerCreated);
    window.removeEventListener('game-joined', this.onGameJoined);
    window.removeEventListener('existing-players-received', this.onExistingPlayersReceived);
    window.removeEventListener('player-stats-update', this.onPlayerStatsUpdate);
    window.removeEventListener('player-hit', this.onPlayerHit);
    
    // Dispose of the scene if it exists
    if (mainSceneRef.value) {
      mainSceneRef.value.dispose();
    }
  },
  methods: {
    initGame() {
      try {
        console.log('Initializing game scene...');
        const canvas = this.$refs.gameCanvas;
        
        if (!canvas) {
          throw new Error('Game canvas element not found');
        }
        
        // Create a new scene instance - MainScene constructor already calls init()
        mainSceneRef.value = new MainScene(canvas);
        
        // Set current player ID from socket ID if available
        if (mainSceneRef.value.socket && mainSceneRef.value.socket.id) {
          this.currentPlayerId = mainSceneRef.value.socket.id;
        } else {
          console.warn('Socket not initialized or no socket ID available');
        }
        
        // Pass player data to the scene if available
        if (this.localPlayerData) {
          console.log('Setting local player data in game scene:', this.localPlayerData);
          mainSceneRef.value.setPlayerData(this.localPlayerData);
        } else if (this.playerData) {
          console.log('Setting player data in game scene:', this.playerData);
          mainSceneRef.value.setPlayerData(this.playerData);
        } else {
          console.warn('No player data available to set in game scene');
        }
        
        // Listen for socket events related to match timer
        this.setupSocketHandlers();
        
        // Start animation loop
        mainSceneRef.value.start();
        
        console.log('Game scene initialized successfully');
      } catch (error) {
        console.error('Failed to initialize game scene:', error);
        this.hasError = true;
        this.errorMessage = 'Failed to initialize the game: ' + error.message;
      }
    },
    
    setupEventListeners() {
      // Keyboard events
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('keyup', this.onKeyUp);
      
      // Mouse events
      document.addEventListener('click', this.onMouseClick);
      document.addEventListener('mousemove', this.onMouseMove);
      
      // Listen for weapon system ready event
      window.addEventListener('weapon-system-ready', this.onWeaponSystemReady);
      
      console.log('Game event listeners set up');
    },
    
    onKeyDown(event) {
      // Initialize keysPressed object if it doesn't exist
      if (!this.keysPressed) {
        this.keysPressed = {};
      }
      
      this.keysPressed[event.code] = true;
      
      // Forward the event to MainScene if it's available
      if (mainSceneRef.value) {
        mainSceneRef.value.onKeyDown(event);
      }
      
      // Handle specific key presses
      switch (event.code) {
        case 'Escape':
          this.togglePause();
          break;
        case 'Tab':
          event.preventDefault();
          // Only handle tab key press if it's not already being handled
          if (!this.tabKeyHandled) {
            this.tabKeyHandled = true;
            // Clean up any duplicate players that might exist before showing scoreboard
            this.cleanupDuplicatePlayers();
            this.isScoreboardVisible = true;
          }
          break;
        case 'KeyR':
          // Reload weapon - MainScene will handle this directly, 
          // but we can update our UI state too
          if (mainSceneRef.value && mainSceneRef.value.weaponSystem) {
            this.isReloading = true;
            // The weaponUpdateTimer will update our UI when reloading is complete
          }
          break;
        case 'Digit1':
        case 'Digit2':
        case 'Digit3':
          // Weapon switching is handled by MainScene directly,
          // but we'll also handle it here for UI state
          const keyNumber = parseInt(event.code.replace('Digit', ''));
          this.switchWeaponByKey(keyNumber);
          break;
      }
    },
    
    // Switch weapon by key number
    switchWeaponByKey(keyNumber) {
      if (mainSceneRef.value && mainSceneRef.value.weaponSystem) {
        mainSceneRef.value.weaponSystem.switchWeaponByKey(keyNumber);
      }
    },
    
    // Switch weapon by type (called from WeaponHUD)
    switchWeapon(weaponType) {
      if (mainSceneRef.value && mainSceneRef.value.weaponSystem) {
        mainSceneRef.value.weaponSystem.switchWeapon(weaponType);
      }
    },
    
    // Update weapon status from the MainScene
    updateWeaponStatus() {
      if (!mainSceneRef.value) {
        console.log('MainScene not available yet');
        return;
      }
      
      // Check if weaponSystem exists
      if (!mainSceneRef.value.weaponSystem) {
        // Only log every 10th time to avoid console spam
        if (Math.random() < 0.1) {
          console.log('Weapon system not initialized yet - waiting...');
        }
        return;
      }
      
      try {
        const weaponSystem = mainSceneRef.value.weaponSystem;
        
        // Update weapon type
        this.weaponType = weaponSystem.currentWeapon;
        
        // Update ammo count
        this.weaponAmmo = weaponSystem.getCurrentAmmo();
        
        // Update weapon state
        this.isReloading = weaponSystem.state.isReloading;
        this.isSwitching = weaponSystem.state.isSwitching;
        
        // Handle total ammo (we're simplifying here)
        this.totalAmmo = this.weaponType === 'knife' ? Infinity : weaponSystem.ammo[this.weaponType];
        
        // Only log the first successful update
        if (!this.weaponStatusInitialized) {
          console.log('Weapon status initialized successfully:', {
            type: this.weaponType,
            ammo: this.weaponAmmo,
            total: this.totalAmmo,
            isReloading: this.isReloading,
            isSwitching: this.isSwitching
          });
          this.weaponStatusInitialized = true;
        }
      } catch (error) {
        console.error('Error updating weapon status:', error);
      }
    },
    
    onKeyUp(event) {
      // Only respond to keyboard events if the player has joined
      if (!this.playerJoined) return;
      
      // Forward the event to MainScene if it's available
      if (mainSceneRef.value) {
        mainSceneRef.value.onKeyUp(event);
      }
      
      // Handle specific key releases
      if (event.code === 'Tab') {
        this.isScoreboardVisible = false;
        this.tabKeyHandled = false; // Reset the tab key flag when released
      }
    },
    
    onMouseClick(event) {
      // Only process mouse clicks if the player has joined
      if (!this.playerJoined || this.isGameOver) return;
      
      // Forward the event to MainScene if it's available
      if (mainSceneRef.value) {
        mainSceneRef.value.onMouseClick(event);
      }
    },
    
    onMouseMove(event) {
      // Only process mouse movement if the player has joined
      if (!this.playerJoined || this.isGameOver) return;
      
      // Forward the event to MainScene if it's available
      if (mainSceneRef.value) {
        mainSceneRef.value.onMouseMove(event);
      }
    },
    
    togglePause() {
      this.isPaused = !this.isPaused;
      
      if (mainSceneRef.value) {
        if (this.isPaused) {
          mainSceneRef.value.stop();
        } else {
          mainSceneRef.value.start();
        }
      }
    },
    
    pauseGame() {
      // Set paused state to true
      this.isPaused = true;
      
      // Stop the scene if available
      if (mainSceneRef.value) {
        mainSceneRef.value.stop();
      }
    },
    
    resumeGame() {
      this.isPaused = false;
      if (mainSceneRef.value) {
        mainSceneRef.value.start();
      }
    },
    
    openSettings() {
      // Open settings modal
      console.log('Settings opened');
    },
    
    exitToLobby() {
      // Navigate to lobby
      console.log('Exiting to lobby');
      this.cleanup(); // Make sure to clean up resources
      this.$emit('exit-to-lobby'); // Emit event to parent component
      // this.$router.push('/lobby'); // Will be implemented with Vue Router
    },
    
    setupSocketHandlers() {
      if (!mainSceneRef.value || !mainSceneRef.value.socket) return;
      
      const socket = mainSceneRef.value.socket;
      
      // Listen for player stats update
      socket.on('player-stats-update', (data) => {
        console.log('Received player stats update:', data);
        this.playerStats = data;
        this.enoughPlayers = data.enoughPlayers;
        
        // Don't request player list on every stats update
        // Instead, check if it's been a while since our last request
        const now = Date.now();
        if (!this.lastRefreshTime || now - this.lastRefreshTime >= 5000) {
          console.log('Requesting player list after stats update');
          socket.emit('request-existing-players');
          this.lastRefreshTime = now;
        }
        
        // Show notification when enough players join
        if (data.enoughPlayers && !this.matchInProgress && !this.showCountdown) {
          this.showNotification('Enough players have joined. Ready to start!', 'info');
        }
      });
      
      // Listen for match countdown
      socket.on('match-countdown', (data) => {
        this.matchCountdown = data.seconds;
        this.showCountdown = true;
        this.showNotification(`Match starting in ${data.seconds}...`, 'info');
      });
      
      // Listen for match start
      socket.on('match-start', (data) => {
        this.matchTimeSeconds = data.durationSeconds;
        this.matchInProgress = true;
        this.showCountdown = false;
        this.isGameOver = false; // Reset game over state when a new match starts
        
        // Show notification
        this.showNotification('Match started!', 'success');
      });
      
      // Listen for match time updates
      socket.on('match-time-update', (data) => {
        this.matchTimeSeconds = data.seconds;
        
        // Show warning notifications at specific times
        if (data.seconds === 60) {
          this.showNotification('1 minute remaining!', 'warning');
        } else if (data.seconds === 30) {
          this.showNotification('30 seconds remaining!', 'warning');
        } else if (data.seconds === 10) {
          this.showNotification('10 seconds remaining!', 'warning');
        }
      });
      
      // Listen for match end
      socket.on('match-end', (data) => {
        console.log('Match ended:', data);
        this.matchInProgress = false;
        this.isGameOver = true;
        this.winner = data.winner;
        this.matchTimeSeconds = 0;
        this.matchDuration = data.duration;
        
        // Prepare player statistics for match summary
        this.prepareMatchSummaryData(data);
        
        // Show game over notification with winner
        this.showNotification(`Game Over! ${data.winner} wins!`, 'success', 5000);
      });
      
      // Check if player joined during an active match
      socket.on('welcome', (data) => {
        console.log('Received welcome message:', data);
        
        // Set the current player ID (local player)
        this.currentPlayerId = data.yourId;
        console.log('Current player ID set to:', this.currentPlayerId);
        
        // Update local game state
        this.playerStats = data.playerStats;
        this.enoughPlayers = data.playerStats.enoughPlayers;
        this.matchInProgress = data.matchInProgress;
        this.matchTimeSeconds = data.matchTimeRemaining;
        this.showCountdown = data.countdownInProgress;
        
        // Make sure any existing player with this ID is marked as local
        const localPlayer = this.players.find(p => p.id === this.currentPlayerId);
        if (localPlayer) {
          localPlayer.isLocalPlayer = true;
          console.log('Updated existing player as local:', localPlayer.username);
        }
        
        // Run cleanup to ensure no duplicates of the local player
        this.cleanupDuplicatePlayers();
        
        // Request existing players data
        socket.emit('request-existing-players');
      });
      
      // Handle scores update
      socket.on('scores-update', (data) => {
        console.log('Scores updated:', data);
        this.playerScores = data;
        
        // Update player list with scores
        this.updatePlayersWithScores(data);
        
        // Clean up any duplicate players that might exist
        this.cleanupDuplicatePlayers();
        
        // Show notification about top player
        const topPlayer = this.getTopPlayer();
        if (topPlayer && this.matchInProgress) {
          this.showNotification(`${topPlayer.username} is leading with ${topPlayer.score} points`, 'info', 2000);
        }
      });
      
      // Handle player joined event
      socket.on('player-joined', (data) => {
        console.log('Received player-joined event from socket:', data);
        
        // Check if this is the local player
        const isLocalPlayer = data.id === this.currentPlayerId;
        
        // Add player to our players array if they don't exist
        const existingPlayer = this.players.find(p => p.id === data.id);
        if (!existingPlayer) {
          this.players.push({
            id: data.id,
            username: isLocalPlayer ? this.displayJoinName : data.username,
            team: data.team || 'Neutral',
            character: data.character || 'soldier',
            score: 0,
            kills: 0,
            deaths: 0,
            assists: 0,
            joinTimestamp: Date.now(),
            isLocalPlayer: isLocalPlayer
          });
          console.log('Added new player to scoreboard from event:', isLocalPlayer ? this.displayJoinName : data.username);
        } else {
          // Update existing player information
          existingPlayer.username = isLocalPlayer ? this.displayJoinName : data.username;
          existingPlayer.team = data.team || 'Neutral';
          existingPlayer.character = data.character || 'soldier';
          existingPlayer.isLocalPlayer = isLocalPlayer;
          console.log('Updated existing player information:', isLocalPlayer ? this.displayJoinName : data.username);
        }
        
        // Clean up any duplicates that might exist
        this.cleanupDuplicatePlayers();
      });
      
      // Handle existing players information
      socket.on('existing-players', (players) => {
        console.log('Received existing players data from socket:', players);
        
        // Create a new map of players to ensure we have everyone
        const updatedPlayerMap = {};
        
        // First, add all existing players we already know about
        this.players.forEach(player => {
          if (!updatedPlayerMap[player.id]) {
            // Preserve the isLocalPlayer flag
            updatedPlayerMap[player.id] = {
              ...player,
              isLocalPlayer: player.id === this.currentPlayerId
            };
          }
        });
        
        // Then, add or update players from the server data
        Object.keys(players).forEach(playerId => {
          const player = players[playerId];
          if (player.username) { // Only add players with a username
            if (updatedPlayerMap[playerId]) {
              // Update existing player data if needed
              updatedPlayerMap[playerId].username = player.username;
              updatedPlayerMap[playerId].team = player.team || 'Neutral';
              updatedPlayerMap[playerId].character = player.character || 'soldier';
              // Update player stats if provided
              if (player.score !== undefined) updatedPlayerMap[playerId].score = player.score;
              if (player.kills !== undefined) updatedPlayerMap[playerId].kills = player.kills;
              if (player.deaths !== undefined) updatedPlayerMap[playerId].deaths = player.deaths;
              if (player.assists !== undefined) updatedPlayerMap[playerId].assists = player.assists;
              // Ensure isLocalPlayer flag is set
              updatedPlayerMap[playerId].isLocalPlayer = playerId === this.currentPlayerId;
            } else {
              // Add new player
              updatedPlayerMap[playerId] = {
                id: playerId,
                username: player.username,
                team: player.team || 'Neutral',
                character: player.character || 'soldier',
                score: player.score || 0,
                kills: player.kills || 0,
                deaths: player.deaths || 0,
                assists: player.assists || 0,
                joinTimestamp: player.joinTimestamp || Date.now(),
                isLocalPlayer: playerId === this.currentPlayerId
              };
              console.log('Added missing player to scoreboard from socket event:', player.username);
            }
          }
        });
        
        // Update players array with the complete player list
        this.players = Object.values(updatedPlayerMap);
        
        // Make sure we don't have any duplicates
        this.cleanupDuplicatePlayers();
        
        // Log the complete player list for debugging
        console.log('Updated player list from socket event:', this.players.map(p => `${p.username} (${p.isLocalPlayer ? 'local' : 'remote'})`));
        
        // Check if we have enough players now
        if (this.getTotalPlayerCount() >= 2) {
          this.enoughPlayers = true;
          this.showNotification('Enough players have joined. Ready to start!', 'info');
        }
      });
    },
    
    // Update players array with scores from the score update
    updatePlayersWithScores(scoreData) {
      // Create a map of existing players by ID
      const playerMap = {};
      
      // First add existing players to the map
      this.players.forEach(player => {
        // Only add the player if they're not already in the map
        if (!playerMap[player.id]) {
          playerMap[player.id] = player;
        }
      });
      
      // Update player scores and add new players if they don't exist
      Object.keys(scoreData).forEach(playerId => {
        const scoreInfo = scoreData[playerId];
        const isLocalPlayer = playerId === this.currentPlayerId;
        
        if (playerMap[playerId]) {
          // Update existing player
          playerMap[playerId].score = scoreInfo.score || 0;
          playerMap[playerId].kills = scoreInfo.kills || 0;
          playerMap[playerId].deaths = scoreInfo.deaths || 0;
          if (scoreInfo.assists) playerMap[playerId].assists = scoreInfo.assists;
          playerMap[playerId].isLocalPlayer = isLocalPlayer;
          
          // Always use display name for local player
          if (isLocalPlayer && this.displayJoinName) {
            playerMap[playerId].username = this.displayJoinName;
          }
        } else if (scoreInfo.username) {
          // Create new player entry if it doesn't exist but we have a username
          playerMap[playerId] = {
            id: playerId,
            username: isLocalPlayer ? (this.displayJoinName || scoreInfo.username) : scoreInfo.username,
            team: scoreInfo.team || 'Neutral',
            character: scoreInfo.character || 'soldier',
            score: scoreInfo.score || 0,
            kills: scoreInfo.kills || 0,
            deaths: scoreInfo.deaths || 0,
            assists: scoreInfo.assists || 0,
            joinTimestamp: scoreInfo.joinTimestamp || Date.now(),
            isLocalPlayer: isLocalPlayer
          };
          console.log('Added new player from score data:', isLocalPlayer ? this.displayJoinName : scoreInfo.username);
        }
      });
      
      // Push updated player objects back to players array
      this.players = Object.values(playerMap);
      
      // Clean up any duplicates
      this.cleanupDuplicatePlayers();
    },
    
    // Get the top-scoring player
    getTopPlayer() {
      if (!this.players || this.players.length === 0) return null;
      return [...this.players].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
    },
    
    // Update a player's information
    updatePlayer(playerId, playerData) {
      // Check if this is the local player
      const isLocalPlayer = playerId === this.currentPlayerId;
      
      // Find the player in our array
      const playerIndex = this.players.findIndex(p => p.id === playerId);
      
      if (playerIndex !== -1) {
        // Update existing player
        this.players[playerIndex] = {
          ...this.players[playerIndex],
          ...playerData,
          isLocalPlayer: isLocalPlayer
        };
      } else if (playerData.username) {
        // Add new player if they have a username
        this.players.push({
          id: playerId,
          username: playerData.username,
          team: playerData.team || 'Neutral',
          character: playerData.character || 'soldier',
          score: 0,
          kills: 0,
          deaths: 0,
          assists: 0,
          joinTimestamp: Date.now(),
          isLocalPlayer: isLocalPlayer
        });
      }
      
      // Clean up any duplicates
      this.cleanupDuplicatePlayers();
    },
    
    cleanup() {
      try {
        // Remove event listeners
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('keyup', this.onKeyUp);
        document.removeEventListener('click', this.onMouseClick);
        document.removeEventListener('mousemove', this.onMouseMove);
        
        // Stop all timers and intervals
        if (this.weaponUpdateTimer) {
          clearInterval(this.weaponUpdateTimer);
          this.weaponUpdateTimer = null;
        }
        
        if (this.playerListRefreshInterval) {
          clearInterval(this.playerListRefreshInterval);
          this.playerListRefreshInterval = null;
        }
        
        if (this.nextGameInterval) {
          clearInterval(this.nextGameInterval);
          this.nextGameInterval = null;
        }
        
        // Clean up Three.js resources - safely check if mainScene exists and has a value
        if (mainSceneRef.value) {
          mainSceneRef.value.dispose();
          mainSceneRef.value = null;
        }
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    },
    
    handleError(error) {
      this.hasError = true;
      this.errorMessage = error.message || 'An error occurred while loading the game';
      
      console.error('Game error:', error);
      
      // Try to clean up any resources that might be causing issues
      try {
        this.cleanup();
      } catch (cleanupError) {
        console.error('Error during cleanup in error handler:', cleanupError);
      }
      
      // Reset important state variables
      this.isPaused = false;
      this.isGameOver = false;
      
      // Provide detailed error info in the console for debugging
      console.info('If you\'re seeing a white screen with only UI elements, the 3D scene might not be rendering properly. Common causes:');
      console.info('1. The canvas element might not be properly initialized');
      console.info('2. The renderer might not be attached to the DOM');
      console.info('3. WebGL might not be supported or enabled in your browser');
      console.info('4. There might be an error in the animation loop or scene setup');
      
      // Give recovery suggestions
      this.showNotification('Error loading game. Try refreshing the page or check the console for details.', 'error');
    },
    
    // Handle health update events from MainScene
    onHealthUpdate(event) {
      const data = event.detail;
      
      // Only update if it's the local player
      if (data.isLocalPlayer) {
        this.playerHealth = data.health;
        this.maxHealth = data.maxHealth;
        
        // If player was eliminated, you could show a death overlay
        if (data.eliminated) {
          console.log('Player eliminated, waiting for respawn...');
          // You could add a death overlay or effect here
        }
      }
    },
    
    // Handle player respawn events
    onPlayerRespawn(event) {
      const data = event.detail;
      
      // Only update if it's the local player
      if (data.isLocalPlayer) {
        this.playerHealth = data.health;
        console.log('Player respawned with health:', data.health);
        
        // Show respawn notification
        this.showNotification('You have respawned!', 'success');
        
        // If position data is available, log it
        if (data.position) {
          console.log('Respawned at position:', data.position);
        }
        
        // Handle spawn protection
        if (data.spawnProtected) {
          this.startSpawnProtection();
        }
      }
    },
    
    // Start spawn protection countdown
    startSpawnProtection() {
      this.hasSpawnProtection = true;
      this.spawnProtectionTimeLeft = 3;
      
      // Clear any existing interval
      if (this.spawnProtectionInterval) {
        clearInterval(this.spawnProtectionInterval);
      }
      
      // Start countdown timer
      this.spawnProtectionInterval = setInterval(() => {
        this.spawnProtectionTimeLeft -= 1;
        
        if (this.spawnProtectionTimeLeft <= 0) {
          // Clear spawn protection when time runs out
          this.clearSpawnProtection();
        }
      }, 1000);
      
      // Show notification
      this.showNotification('Spawn protection active for 3 seconds', 'info');
    },
    
    // Clear spawn protection
    clearSpawnProtection() {
      this.hasSpawnProtection = false;
      
      // Clear interval if it exists
      if (this.spawnProtectionInterval) {
        clearInterval(this.spawnProtectionInterval);
        this.spawnProtectionInterval = null;
      }
    },
    
    // Show a notification message
    showNotification(message, type = 'info', duration = 4000) {
      this.notification = {
        message,
        type,
        visible: true
      };
      
      // Hide notification after specified duration
      setTimeout(() => {
        this.notification.visible = false;
      }, duration);
    },
    
    // Update game state from the scene
    updateGameState() {
      if (!mainSceneRef.value) return;
      
      // Update health from scene if available
      if (typeof mainSceneRef.value.playerHealth !== 'undefined') {
        this.playerHealth = mainSceneRef.value.playerHealth;
      }
      
      if (typeof mainSceneRef.value.maxHealth !== 'undefined') {
        this.maxHealth = mainSceneRef.value.maxHealth;
      }
      
      // ... rest of updateGameState ...
    },
    
    tryAgain() {
      console.log('Attempting to reinitialize the game...');
      
      // Reset error state
      this.hasError = false;
      this.errorMessage = null;
      
      // Clean up any existing resources
      this.cleanup();
      
      // Delay initialization slightly to ensure cleanup is complete
      setTimeout(() => {
        try {
          this.initGame();
          
          if (!this.hasError) {
            this.setupEventListeners();
            this.showNotification('Game reinitialized successfully!', 'success');
          }
        } catch (error) {
          console.error('Failed to reinitialize game:', error);
          this.handleError(error);
        }
      }, 500);
    },
    
    // Handle player shooting to clear spawn protection
    onPlayerShoot(event) {
      const data = event.detail;
      
      // Only clear spawn protection if it's active and the player shooting is the local player
      if (this.hasSpawnProtection && data.isLocalPlayer) {
        this.clearSpawnProtection();
      }
      
      // Add crosshair expansion effect when local player shoots
      if (data.isLocalPlayer) {
        this.isShooting = true;
        
        // Clear any existing timeout
        if (this.crosshairTimeout) {
          clearTimeout(this.crosshairTimeout);
        }
        
        // Set timeout to revert crosshair after a delay
        this.crosshairTimeout = setTimeout(() => {
          this.isShooting = false;
        }, 200); // Adjust this time to control how long the crosshair stays expanded
      }
    },
    
    // Navigate to match summary view
    viewMatchSummary() {
      if (!mainSceneRef.value) return;
      
      // Dispose of the scene before switching views
      mainSceneRef.value.dispose();
      
      // Determine the game mode
      const isTeamMode = this.playerData?.gameMode !== 'FreeForAll';
      
      // Pass match data to the parent component and request view change
      this.$emit('view-match-summary', {
        matchId: `M${Date.now().toString().substr(-4)}`,
        map: 'Warehouse',
        matchDuration: this.matchDuration,
        // Only include team scores in team mode
        ...(isTeamMode ? {
          redTeamScore: this.redTeamScore,
          blueTeamScore: this.blueTeamScore,
        } : {}),
        winner: this.winner,
        players: this.players.map(player => ({
          id: player.id,
          username: player.username,
          team: player.team || 'Neutral',
          character: player.character,
          kills: player.kills || 0,
          deaths: player.deaths || 0,
          assists: player.assists || 0
        })),
        events: this.matchEvents
      });
    },
    
    // Prepare match summary data from available information
    prepareMatchSummaryData(matchEndData) {
      // Create match events list if it doesn't exist
      if (!this.matchEvents) {
        this.matchEvents = [
          { type: 'start', timeSeconds: 0, text: 'Match started' }
        ];
      }
      
      // Add match end event
      this.matchEvents.push({
        type: 'end',
        timeSeconds: this.matchDuration,
        winner: this.winner,
        text: `Match ended. ${this.winner} wins!`
      });
      
      // Ensure players have stats initialized
      this.players.forEach(player => {
        if (!player.kills) player.kills = 0;
        if (!player.deaths) player.deaths = 0;
        if (!player.assists) player.assists = 0;
      });
    },
    
    // Handle match start events from MainScene
    onMatchStart(event) {
      const data = event.detail;
      console.log('Received match-start event from MainScene:', data);
      
      // Update UI state
      this.matchTimeSeconds = data.durationSeconds;
      this.matchInProgress = true;
      this.showCountdown = false;
      this.isGameOver = false; // Reset game over state when a new match starts
      
      // Show notification
      this.showNotification('Match started!', 'success');
    },
    
    // Handle match end events from MainScene
    onMatchEnd(event) {
      const data = event.detail;
      console.log('Received match-end event from MainScene:', data);
      
      // Update UI state
      this.matchInProgress = false;
      this.isGameOver = true;
      this.winner = data.winner;
      this.matchTimeSeconds = 0;
      
      // Set team scores if available in the event data
      if (data.teamScores) {
        this.redTeamScore = data.teamScores.Red;
        this.blueTeamScore = data.teamScores.Blue;
      }
      
      // Set match duration if available
      if (data.duration) {
        this.matchDuration = data.duration;
      }
      
      // Prepare player statistics for match summary
      this.prepareMatchSummaryData(data);
      
      // Show game over notification with winner
      this.showNotification(`Game Over! ${data.winner} wins!`, 'success', 5000);
    },
    
    // Handle player creation event from MainScene
    onPlayerCreated(event) {
      const data = event.detail;
      console.log('Received player-created event from MainScene:', data);
      
      // Set current player ID if it's our local player
      if (!this.currentPlayerId) {
        this.currentPlayerId = data.playerId;
      }
      
      // Check if this is the local player
      const isLocalPlayer = data.playerId === this.currentPlayerId;
      
      // Add to players array if not exists
      const existingPlayer = this.players.find(p => p.id === data.playerId);
      if (!existingPlayer) {
        this.players.push({
          id: data.playerId,
          username: this.playerData?.username || 'Player',
          team: data.team || 'Neutral',
          character: data.character || 'soldier',
          score: 0,
          kills: 0,
          deaths: 0,
          assists: 0,
          joinTimestamp: Date.now(),
          isLocalPlayer: isLocalPlayer
        });
        
        // Check if we have enough players now
        if (this.getTotalPlayerCount() >= 2) {
          this.enoughPlayers = true;
          this.showNotification('Enough players have joined. Ready to start!', 'info');
        }
      } else {
        // Update existing player
        existingPlayer.isLocalPlayer = isLocalPlayer;
      }
      
      // Clean up any duplicates
      this.cleanupDuplicatePlayers();
    },
    
    // Handle player joined event
    onPlayerJoined(event) {
      const data = event.detail;
      console.log('Received player-joined event from socket:', data);
      
      // Check if this is the local player
      const isLocalPlayer = data.id === this.currentPlayerId;
      
      // Add player to our players array if they don't exist
      const existingPlayer = this.players.find(p => p.id === data.id);
      if (!existingPlayer) {
        this.players.push({
          id: data.id,
          username: isLocalPlayer ? this.displayJoinName : data.username,
          team: data.team || 'Neutral',
          character: data.character || 'soldier',
          score: 0,
          kills: 0,
          deaths: 0,
          assists: 0,
          joinTimestamp: Date.now(),
          isLocalPlayer: isLocalPlayer
        });
        console.log('Added new player to scoreboard from event:', isLocalPlayer ? this.displayJoinName : data.username);
      } else {
        // Update existing player information
        existingPlayer.username = isLocalPlayer ? this.displayJoinName : data.username;
        existingPlayer.team = data.team || 'Neutral';
        existingPlayer.character = data.character || 'soldier';
        existingPlayer.isLocalPlayer = isLocalPlayer;
        console.log('Updated existing player information:', isLocalPlayer ? this.displayJoinName : data.username);
      }
      
      // Clean up any duplicates that might exist
      this.cleanupDuplicatePlayers();
    },
    
    // Handle when the local player has joined the game
    onGameJoined(event) {
      const data = event.detail;
      console.log('Received game-joined event:', data);
      
      // Add new player to the existing player list if this player doesn't exist
      const existingPlayer = this.players.find(p => p.id === data.id);
      
      if (!existingPlayer) {
        // Data passed from the server may have username, character, etc.
        // Create new player entry with data values or defaults
        this.players.push({
          id: data.id,
          username: data.username || this.localPlayerData?.username || this.playerData?.username || 'Player',
          character: data.character || this.localPlayerData?.character || this.playerData?.character || 'soldier',
          score: 0,
          kills: 0,
          deaths: 0,
          assists: 0,
          joinTimestamp: Date.now(),
          isLocalPlayer: data.id === this.currentPlayerId
        });
      } else {
        // Update existing player's information
        existingPlayer.username = data.username || this.localPlayerData?.username || existingPlayer.username;
        existingPlayer.character = data.character || this.localPlayerData?.character || existingPlayer.character;
        existingPlayer.isLocalPlayer = data.id === this.currentPlayerId;
      }
      
      // If this is the local player, ensure we have the current ID
      if (data.isLocalPlayer || data.id === this.currentPlayerId) {
        // Update the current player ID
        this.currentPlayerId = data.id;
        
        // Make sure we update our player with the proper username from form
        const localPlayer = this.players.find(p => p.id === this.currentPlayerId);
        if (localPlayer) {
          // Use display name from join form which includes the unique ID
          localPlayer.username = this.displayJoinName || this.localPlayerData?.username || 'Player';
          localPlayer.isLocalPlayer = true;
        }
      }
      
      // Clean up any duplicates that might exist
      this.cleanupDuplicatePlayers();
    },
    
    // Get the total player count including local player if needed
    getTotalPlayerCount() {
      // If we have players in our array, use that length (includes local player)
      if (this.players.length > 0) {
        return this.players.length;
      }
      
      // Otherwise use playerStats and add 1 if local player exists but isn't counted
      const serverCount = this.playerStats.players || 0;
      const localPlayerExists = (this.localPlayerData || this.playerData) && this.currentPlayerId;
      const localPlayerCounted = this.players.some(p => p.id === this.currentPlayerId);
      
      // Add 1 for local player if they exist but aren't counted in playerStats
      return serverCount + (localPlayerExists && !localPlayerCounted ? 1 : 0);
    },
    
    // Handle receiving existing players event from MainScene
    onExistingPlayersReceived(event) {
      const { players } = event.detail;
      console.log('Received existing-players-received event from MainScene:', players);
      
      // Create a new map of players to ensure we have everyone
      const updatedPlayerMap = {};
      
      // First, add all existing players we already know about
      this.players.forEach(player => {
        if (!updatedPlayerMap[player.id]) {
          // Preserve the isLocalPlayer flag
          updatedPlayerMap[player.id] = {
            ...player,
            isLocalPlayer: player.id === this.currentPlayerId
          };
        }
      });
      
      // Then, add or update players from the server data
      Object.keys(players).forEach(playerId => {
        const player = players[playerId];
        if (player.username) { // Only add players with a username
          if (updatedPlayerMap[playerId]) {
            // Update existing player data if needed
            updatedPlayerMap[playerId].username = player.username;
            updatedPlayerMap[playerId].team = player.team || 'Neutral';
            updatedPlayerMap[playerId].character = player.character || 'soldier';
            // Update player stats if provided
            if (player.score !== undefined) updatedPlayerMap[playerId].score = player.score;
            if (player.kills !== undefined) updatedPlayerMap[playerId].kills = player.kills;
            if (player.deaths !== undefined) updatedPlayerMap[playerId].deaths = player.deaths;
            if (player.assists !== undefined) updatedPlayerMap[playerId].assists = player.assists;
            // Ensure isLocalPlayer flag is set
            updatedPlayerMap[playerId].isLocalPlayer = playerId === this.currentPlayerId;
          } else {
            // Add new player
            updatedPlayerMap[playerId] = {
              id: playerId,
              username: player.username,
              team: player.team || 'Neutral',
              character: player.character || 'soldier',
              score: player.score || 0,
              kills: player.kills || 0,
              deaths: player.deaths || 0,
              assists: player.assists || 0,
              joinTimestamp: player.joinTimestamp || Date.now(),
              isLocalPlayer: playerId === this.currentPlayerId
            };
            console.log('Added missing player to scoreboard from MainScene event:', player.username);
          }
        }
      });
      
      // Update players array with the complete player list
      this.players = Object.values(updatedPlayerMap);
      
      // Make sure we don't have any duplicates
      this.cleanupDuplicatePlayers();
      
      // Log the complete player list for debugging
      console.log('Updated player list from MainScene event:', this.players.map(p => p.username));
      
      // Check if we have enough players now
      if (this.getTotalPlayerCount() >= 2) {
        this.enoughPlayers = true;
        this.showNotification('Enough players have joined. Ready to start!', 'info');
      }
    },
    
    // Request updated player list from server
    refreshPlayerList() {
      if (!mainSceneRef.value || !mainSceneRef.value.socket) return;
      
      this.showNotification('Refreshing player list...', 'info');
      mainSceneRef.value.socket.emit('request-existing-players');
    },
    
    // Add handler for player stats update events
    onPlayerStatsUpdate(event) {
      const stats = event.detail;
      console.log('Received player-stats-update event from MainScene:', stats);
      
      this.playerStats = stats;
      this.enoughPlayers = stats.enoughPlayers;
      
      // Show notification when enough players join
      if (stats.enoughPlayers && !this.matchInProgress && !this.showCountdown) {
        this.showNotification('Enough players have joined. Ready to start!', 'info');
      }
      
      // Request the player list to ensure we have all players
      if (mainSceneRef.value && mainSceneRef.value.socket) {
        mainSceneRef.value.socket.emit('request-existing-players');
      }
    },
    
    // Handle when a player disconnects
    handlePlayerDisconnect(playerId) {
      // Find the player and remove them
      const playerIndex = this.players.findIndex(p => p.id === playerId);
      if (playerIndex !== -1) {
        this.players.splice(playerIndex, 1);
        this.showNotification(`Player disconnected`, 'info');
        console.log(`Player ${playerId} disconnected`);
      }
    },
    
    // Method to handle game end
    handleGameOver(winnerData) {
      this.isGameOver = true;
      this.winner = winnerData.winner;
      this.matchDuration = winnerData.matchDuration || 300; // Default to 5 minutes if not provided
      
      // Clear any existing intervals to be safe
      if (this.nextGameInterval) {
        clearInterval(this.nextGameInterval);
      }
      
      // Start the countdown for next game
      this.nextGameCountdown = 20;
      this.nextGameInterval = setInterval(() => {
        this.nextGameCountdown--;
        
        if (this.nextGameCountdown <= 0) {
          clearInterval(this.nextGameInterval);
          this.resetGameForNextMatch();
        }
      }, 1000);
    },
    
    // Reset the game state for the next match
    resetGameForNextMatch() {
      this.isGameOver = false;
      this.winner = null;
      this.nextGameCountdown = 20;
      
      // Reset player stats as needed
      this.players.forEach(player => {
        player.kills = 0;
        player.deaths = 0;
        player.score = 0;
      });
      
      // No need to manually start the match as server handles this automatically
      this.showCountdown = false;
      console.log('Waiting for server to start the next match automatically...');
      this.showNotification('Waiting for server to start next match...', 'info');
    },
    
    // Format time from seconds to MM:SS
    formatTime(totalSeconds) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    
    // Handler for weapon system ready event
    onWeaponSystemReady(event) {
      console.log('Received weapon-system-ready event:', event.detail);
      this.weaponStatusInitialized = false; // Reset to get the initial log
      
      // Force an immediate update of weapon status
      this.updateWeaponStatus();
      
      // Show notification
      this.showNotification('Weapon system loaded', 'info', 2000);
    },
    
    // Clean up any duplicate players that might exist in the players array
    cleanupDuplicatePlayers() {
      console.log("Starting cleanup: ", this.players.length, "players");
      
      // First ensure all players have proper ID
      const playersWithIds = this.players.filter(player => player && player.id);
      if (playersWithIds.length !== this.players.length) {
        console.warn(`Removed ${this.players.length - playersWithIds.length} players without IDs`);
        this.players = playersWithIds;
      }
      
      // Create a map to find duplicates and keep the best one
      const uniquePlayersByID = new Map();
      
      // First prioritize the local player
      const localPlayerIndex = this.players.findIndex(p => p.id === this.currentPlayerId);
      if (localPlayerIndex !== -1) {
        const localPlayer = this.players[localPlayerIndex];
        uniquePlayersByID.set(localPlayer.id, { 
          ...localPlayer, 
          isLocalPlayer: true,
          // Always use the display name for local player if available
          username: this.displayJoinName || localPlayer.username
        });
      }
      
      // Process all players to find and merge duplicates
      this.players.forEach(player => {
        if (!player || !player.id) return;
        
        if (!uniquePlayersByID.has(player.id)) {
          // New player - just add them directly
          uniquePlayersByID.set(player.id, { 
            ...player,
            isLocalPlayer: player.id === this.currentPlayerId,
            // If this is local player, use display name
            username: player.id === this.currentPlayerId ? 
              (this.displayJoinName || player.username) : player.username
          });
        } else if (player.id !== this.currentPlayerId) {
          // Existing non-local player - merge attributes
          const existingPlayer = uniquePlayersByID.get(player.id);
          
          // Keep the most recent timestamp
          if (!existingPlayer.joinTimestamp || 
              (player.joinTimestamp && player.joinTimestamp < existingPlayer.joinTimestamp)) {
            existingPlayer.joinTimestamp = player.joinTimestamp;
          }
          
          // Preserve the highest score and stats
          existingPlayer.score = Math.max(existingPlayer.score || 0, player.score || 0);
          existingPlayer.kills = Math.max(existingPlayer.kills || 0, player.kills || 0);
          existingPlayer.deaths = Math.max(existingPlayer.deaths || 0, player.deaths || 0);
          existingPlayer.assists = Math.max(existingPlayer.assists || 0, player.assists || 0);
          
          // Keep the most recent username if available
          if (player.username && (!existingPlayer.username || existingPlayer.username === 'Player')) {
            existingPlayer.username = player.username;
          }
        } else {
          // Local player duplicate - update attributes carefully
          const existingPlayer = uniquePlayersByID.get(player.id);
          
          // Always ensure the local player flag is set
          existingPlayer.isLocalPlayer = true;
          
          // Always use display name for local player if available
          existingPlayer.username = this.displayJoinName || existingPlayer.username;
          
          // Keep highest stats
          existingPlayer.score = Math.max(existingPlayer.score || 0, player.score || 0);
          existingPlayer.kills = Math.max(existingPlayer.kills || 0, player.kills || 0);
          existingPlayer.deaths = Math.max(existingPlayer.deaths || 0, player.deaths || 0);
          existingPlayer.assists = Math.max(existingPlayer.assists || 0, player.assists || 0);
        }
      });
      
      // Replace the players array with the deduplicated list
      const originalLength = this.players.length;
      this.players = Array.from(uniquePlayersByID.values());
      
      // Log if any duplicates were removed
      if (originalLength !== this.players.length) {
        console.log(`Removed ${originalLength - this.players.length} duplicate player entries from scoreboard`);
      }
      
      // Additional verification - ensure we have the local player correctly marked
      if (this.currentPlayerId) {
        const localPlayer = this.players.find(p => p.id === this.currentPlayerId);
        if (localPlayer) {
          localPlayer.isLocalPlayer = true;
          // Always use display name for local player
          localPlayer.username = this.displayJoinName || localPlayer.username;
        }
      }
      
      console.log("After cleanup: ", this.players.length, "players");
    },
    
    // Handle join game form submission
    handleJoinGame() {
      if (!this.mainScene) {
        console.error('Cannot join game: mainScene not initialized');
        return;
      }
      
      // Create player data from form
      const playerData = {
        username: this.displayJoinName,
        character: 'soldier' // Default to soldier character
      };
      
      console.log('Joining game with data:', playerData);
      
      // Set player joined flag and store data in local property, not the prop
      this.localPlayerData = playerData;
      this.playerJoined = true;
      
      // Make sure we're not paused when joining
      this.isPaused = false;
      
      // Ensure mainScene is directly accessible and has controls enabled
      this.mainScene = mainSceneRef.value;
      if (this.mainScene) {
        // Make sure controls are enabled
        if (this.mainScene.controls) {
          this.mainScene.controls.enabled = true;
        }
        
        // Make sure mouse controls are enabled
        if (this.mainScene.mouse) {
          this.mainScene.mouse.enabled = true;
          this.mainScene.mouse.locked = true;
        }
        
        // Ensure the scene is running, not paused
        this.mainScene.start();
      }
      
      // Reset the pointer lock status to ensure we start fresh
      this.isPointerLocked = false;
      
      // Force pointer lock immediately - using setTimeout to ensure it happens after UI updates
      setTimeout(() => {
        this.gameCanvas = this.$refs.gameCanvas;
        if (this.gameCanvas && document.pointerLockElement !== this.gameCanvas) {
          console.log('Requesting pointer lock immediately after join');
          // Reset any existing pointer lock
          if (document.pointerLockElement) {
            document.exitPointerLock();
          }
          // Request pointer lock after a brief delay
          setTimeout(() => {
            this.gameCanvas.requestPointerLock();
          }, 50);
        }
      }, 100);
      
      // Send join request through main scene
      if (this.mainScene.socket && this.mainScene.socket.connected) {
        this.mainScene.socket.emit('join-game', playerData);
      } else {
        console.error('Cannot join game: socket not connected');
      }
    },
    setupControls() {
      // Setup click handler for canvas to enable pointer lock
      this.gameCanvas = this.$refs.gameCanvas;
      if (!this.gameCanvas) {
        console.error('Cannot set up controls: gameCanvas ref not found');
        return;
      }
      
      // Listen for clicks on the canvas
      this.gameCanvas.addEventListener('click', (event) => {
        // Only request pointer lock if the player has joined and the game is active
        if (this.playerJoined && !this.isPaused && !this.isGameOver && !this.hasError) {
          if (document.pointerLockElement !== this.gameCanvas) {
            this.gameCanvas.requestPointerLock();
          }
        }
      });
      
      // Set up keyboard and pointer lock event listeners
      this.setupKeyboardListeners();
      this.setupPointerLockListeners();
    },
    setupPointerLockListeners() {
      // Track initial join to prevent immediate pause
      let initialJoinInProgress = true;
      
      // Pointer lock change event
      document.addEventListener('pointerlockchange', () => {
        // Only handle pointer lock if the player has joined
        if (!this.playerJoined) return;
        
        if (document.pointerLockElement === this.gameCanvas) {
          console.log('Pointer lock activated');
          this.isPointerLocked = true;
          initialJoinInProgress = false; // No longer in initial join once pointer lock is activated
          
          // Tell the main scene that the mouse is locked
          if (this.mainScene) {
            this.mainScene.mouse.locked = true;
            // Ensure controls are enabled
            if (this.mainScene.controls) {
              this.mainScene.controls.enabled = true;
            }
            // Signal to the socket that mouse controls are enabled
            if (this.mainScene.socket) {
              this.mainScene.socket.emit('controls-enabled');
            }
          }
        } else {
          console.log('Pointer lock deactivated');
          this.isPointerLocked = false;
          
          // Tell the main scene that the mouse is unlocked
          if (this.mainScene) {
            this.mainScene.mouse.locked = false;
          }
          
          // Request pointer lock again to keep playing
          setTimeout(() => {
            if (this.playerJoined && this.gameCanvas && !this.isGameOver) {
              console.log('Requesting pointer lock again');
              this.gameCanvas.requestPointerLock();
            }
          }, 100);
        }
      });
      
      // Pointer lock error event
      document.addEventListener('pointerlockerror', (event) => {
        console.error('Pointer lock error:', event);
        this.isPointerLocked = false;
        if (this.mainScene) {
          this.mainScene.mouse.locked = false;
        }
        
        // Try again during initial join
        if (initialJoinInProgress) {
          setTimeout(() => {
            if (this.gameCanvas && document.pointerLockElement !== this.gameCanvas) {
              this.gameCanvas.requestPointerLock();
            }
          }, 200);
        }
      });
    },
    setupKeyboardListeners() {
      // Only set up keyboard listeners if they don't already exist
      if (this.keyboardListenersInitialized) return;
      
      document.addEventListener('keydown', (event) => {
        // Only respond to keyboard events if the player has joined
        if (!this.playerJoined) return;
        
        // Handle keyboard events...
        this.keysPressed[event.code] = true;
        
        // Handle Tab key for scoreboard
        if (event.code === 'Tab') {
          event.preventDefault(); // Prevent focus changes
          if (!this.tabKeyHandled) {
            this.isScoreboardVisible = true;
            this.tabKeyHandled = true;
          }
        }
      });
      
      document.addEventListener('keyup', (event) => {
        // Only respond to keyboard events if the player has joined
        if (!this.playerJoined) return;
        
        delete this.keysPressed[event.code];
        
        // Handle Tab key for scoreboard
        if (event.code === 'Tab') {
          this.isScoreboardVisible = false;
          this.tabKeyHandled = false;
        }
      });
      
      this.keyboardListenersInitialized = true;
    },
  }
});
</script>

<style>
.game-view {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

#game-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.game-over {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  z-index: 10;
}

.menu-options {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
}

.menu-options button,
.game-over button {
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
}

.game-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  z-index: 10;
}

.game-error p {
  margin-bottom: 20px;
}

.game-notification {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px;
  border-radius: 5px;
  z-index: 10;
}

.notification-info {
  background-color: #3498db;
}

.notification-success {
  background-color: #2ecc71;
}

.notification-warning {
  background-color: #f39c12;
}

.notification-error {
  background-color: #e74c3c;
}

.start-match-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: auto;
}

.start-match-button {
  padding: 12px 25px;
  font-size: 1.2em;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 1px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.start-match-button:hover {
  background-color: #45a049;
  transform: scale(1.05);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.4);
}

.start-match-button:active {
  transform: scale(0.98);
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.3);
}

.waiting-message-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  padding: 20px 30px;
  text-align: center;
  color: white;
  min-width: 300px;
}

.waiting-message h2 {
  margin-top: 0;
  font-size: 1.8em;
  color: #ffffff;
  margin-bottom: 20px;
}

.player-count {
  margin: 15px 0;
  font-size: 1.2rem;
  padding: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 5px;
}

.waiting-message p {
  margin-bottom: 0;
  font-style: italic;
  color: #cccccc;
}

.spawn-protection-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px;
  border-radius: 5px;
  z-index: 10;
}

.protection-icon {
  font-size: 24px;
  margin-bottom: 5px;
}

.protection-text {
  font-size: 16px;
  font-weight: bold;
}

.protection-timer {
  font-size: 14px;
  font-weight: bold;
}

.protection-note {
  font-size: 12px;
  color: #cccccc;
}

/* Crosshair styles */
.crosshair {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  pointer-events: none; /* Makes sure it doesn't interfere with mouse events */
  z-index: 100; /* Ensure it's above the game canvas but below UI elements like pause menu */
  transition: all 0.1s ease-out; /* Smooth transition when not shooting */
}

/* Expanded state when shooting */
.crosshair-shooting {
  width: 30px; /* Wider when shooting */
  height: 30px; /* Taller when shooting */
}

.crosshair-inner {
  position: relative;
  width: 100%;
  height: 100%;
}

.crosshair-inner::before,
.crosshair-inner::after {
  content: '';
  position: absolute;
  background-color: rgba(255, 255, 255, 0.8); /* Semi-transparent white */
  transition: all 0.1s ease-out; /* Smooth transition for lines */
}

/* Vertical line */
.crosshair-inner::before {
  width: 2px;
  height: 100%;
  left: 50%;
  transform: translateX(-50%);
}

/* Horizontal line */
.crosshair-inner::after {
  width: 100%;
  height: 2px;
  top: 50%;
  transform: translateY(-50%);
}

/* Gap in vertical line */
.crosshair-inner::before {
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.8) 40%,
    transparent 40%,
    transparent 60%,
    rgba(255, 255, 255, 0.8) 60%,
    rgba(255, 255, 255, 0.8) 100%
  );
}

/* Gap in horizontal line */
.crosshair-inner::after {
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.8) 40%,
    transparent 40%,
    transparent 60%,
    rgba(255, 255, 255, 0.8) 60%,
    rgba(255, 255, 255, 0.8) 100%
  );
}

/* Make the gap wider when shooting */
.crosshair-shooting .crosshair-inner::before {
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.8) 35%,
    transparent 35%,
    transparent 65%,
    rgba(255, 255, 255, 0.8) 65%,
    rgba(255, 255, 255, 0.8) 100%
  );
}

.crosshair-shooting .crosshair-inner::after {
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.8) 35%,
    transparent 35%,
    transparent 65%,
    rgba(255, 255, 255, 0.8) 65%,
    rgba(255, 255, 255, 0.8) 100%
  );
}

.connected-players {
  margin: 10px 0;
  font-size: 0.9rem;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 5px;
  padding: 8px;
  text-align: left;
}

.connected-players p {
  margin: 0 0 5px 0;
  font-weight: bold;
}

.connected-players ul {
  margin: 0;
  padding-left: 20px;
}

.connected-players li {
  margin-bottom: 5px;
}

.refresh-button {
  margin: 10px auto;
  padding: 8px 15px;
  background-color: #3498db;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.refresh-button:hover {
  background-color: #2980b9;
}

.game-stats-screen {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  z-index: 10;
  width: 80%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.game-stats-screen h2 {
  margin-top: 0;
  font-size: 2rem;
  color: #f8f8f8;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.game-stats-screen h3 {
  font-size: 1.5rem;
  margin-bottom: 25px;
  color: #4caf50;
}

.match-stats-container {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
}

.match-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.info-box {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.info-label {
  font-size: 0.9rem;
  color: #aaa;
  margin-bottom: 5px;
}

.info-value {
  font-size: 1.2rem;
  font-weight: bold;
}

.player-stats-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 auto;
  color: #f8f8f8;
}

.player-stats-table th {
  padding: 12px 8px;
  background-color: rgba(0, 0, 0, 0.3);
  text-align: left;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.player-stats-table td {
  padding: 10px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.player-stats-table tr.current-player {
  background-color: rgba(76, 175, 80, 0.2);
}

.rank {
  width: 40px;
  text-align: center;
}

.player-col {
  width: 40%;
}

.score-cell {
  font-weight: bold;
}

.next-game-countdown {
  margin-top: 20px;
}

.next-game-countdown h3 {
  color: #f8f8f8;
  margin-bottom: 10px;
}

.progress-bar {
  height: 10px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  overflow: hidden;
  margin-top: 10px;
}

.progress {
  height: 100%;
  background-color: #4caf50;
  border-radius: 5px;
  transition: width 1s linear;
}

/* Join game overlay styles */
.join-game-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.join-game-form {
  background-color: white;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.join-game-form h2 {
  margin-top: 0;
  margin-bottom: 20px;
}

.form-description {
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.username-preview {
  margin-bottom: 20px;
}

.join-actions {
  margin-top: 20px;
}

.join-button {
  padding: 10px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}
</style> 